import {
  Button,
  Drawer,
  InsightIcon,
  SkeletonLoader,
} from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import { InsightDirection } from '@toggle/toggle';
import { isSameDay } from 'date-fns';
import React, { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useGetInsightWidgetData } from '~/services/insights/insights-service';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';
import { DownloadButton } from '~/views/stream/components/download-button/DownloadButton';
import { ScheduleDemoBanner } from '~/views/stream/components/schedule-demo-banner/ScheduleDemoBanner';
import { handleDownloadDrawer } from '~/views/stream/utils/handleDownloadDrawer';

import { InsightsDrawerContent } from './components/insights-drawer-content/InsightsDrawerContent';
import styles from './InsightsDrawer.module.scss';

export type InsightsDrawerProps = {
  insightId: string;
};

export const InsightsDrawer = ({ insightId }: InsightsDrawerProps) => {
  useDisableScroll();
  const { t } = useTranslation([
    'widget',
    'common',
    'stream',
    'scenarioCommander',
    'analyze',
    'scenario',
    'screener',
    'insights',
    'chart',
  ]);
  const { closeDrawer } = useInsightDrawer();
  const { data, isFetching } = useGetInsightWidgetData(insightId);

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  // Temp: update to navigate from watchlist when available
  const { navigateToPage } = useNavigateToAssetOverview();

  const navigateToAssetOverview = () => {
    if (data?.data?.entity.tag) {
      navigateToPage({ entityTag: data.data.entity.tag });
    }
  };

  const getDrawerContent = () => {
    if (isFetching) {
      return (
        <div className={styles.loadingSkeletonWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '100%',
                  height: 'calc(100vh - 80px)',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        </div>
      );
    }

    if (data?.error || !data?.data) {
      return <ErrorMessageWrapper variant="empty" />;
    }

    let icon: ReactNode = null;
    const { insight } = data.data;

    const conditions = getConditionIcons({
      conditions: insight.metadata.conditions,
      kind: insight.metadata.kind,
    });

    if (conditions.length > 0) {
      const isCombined = conditions.length > 1;
      const isNewInsight = isSameDay(new Date(insight.created_at), new Date());
      icon = (
        <InsightIconsRoot
          conditions={conditions}
          direction={getExploreDirection(insight.metadata.direction)}
          isStarred={isNewInsight}
          isCombined={isCombined}
        />
      );
    } else {
      const isBullish =
        data.data.insight.metadata.direction === InsightDirection.Bullish;
      const showSparkles = isSameDay(
        new Date(
          data.data.insight.metadata?.last_value_date ??
            data.data.insight.created_at
        ),
        new Date()
      );
      icon = (
        <InsightIcon
          isBullish={isBullish}
          showSparkles={showSparkles}
          options={{ iconSize: 32 }}
        />
      );
    }

    return (
      <div ref={contentWrapperRef}>
        <InsightsDrawerContent
          insightsData={data.data}
          header={() => (
            <div className={styles.header}>
              {icon}
              <div className={styles.title}>
                {data.data.insight?.simple_title ?? data.data.insight.card.name}
              </div>
            </div>
          )}
          showViewMore
        />
        <ScheduleDemoBanner />
      </div>
    );
  };

  const handleDownload = () => {
    if (contentWrapperRef.current) {
      handleDownloadDrawer({
        contentToPrint: contentWrapperRef.current,
        entityTag: data?.data?.entity.tag ?? null,
      });
    }
  };

  return (
    <Drawer
      data-testid="insights-drawer"
      isOpen
      handleCloseClick={closeDrawer}
      headerButton={
        <div className={styles.headerButtons}>
          <Button
            iconName="Expand"
            label={t('widget:assetOverview.assetOverview')}
            onClick={navigateToAssetOverview}
            variant="empty"
            size="xsmall"
          />
          <DownloadButton onClick={handleDownload} />
        </div>
      }
    >
      <WithFooterWrapper
        trackingMetadata={{
          source: POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER,
        }}
      >
        {getDrawerContent()}
      </WithFooterWrapper>
    </Drawer>
  );
};
